// FP 간트(자체 구현) — 행 모델
//
// AG Grid를 쓰지 않는 간트 화면(GanttFrame)이 쓰는 순수 계산 모듈이다. 행 높이가 **가변**이라는 게
// 핵심 — 한 자원 행에서 활동이 겹칠 때 "한 줄에 겹쳐 그리기"와 "트랙으로 수직 나열"을 모두
// 지원해야 하고, 후자는 트랙 수만큼 행이 높아진다(AG Grid에서는 행 높이를 그리드가 소유해서
// 이 요구가 가장 비쌌다 — reports/gantt-implementation.md §7-1).
//
// 여기서는 좌표만 만든다. 렌더는 ganttDraw.js, 배치·스크롤은 GanttFrame.js.

import { groupKeyOf } from "@plannel/pages/factory-plan/common/gantt/adjustShrink";

export const OVERLAP_MODE = {
  OVERLAY: "overlay",   // 한 줄에 겹쳐 그린다(뒤에 시작한 박스가 위) — old ResourceGantt 동작
  STACK: "stack"        // 겹치면 트랙을 늘려 수직으로 나열한다(행 높이 증가)
};

/**
 * 각 박스에 **양옆이 딱 붙어 있는지**(`abutPrev` / `abutNext`)를 표시한다 — `drawBox`가 맞닿은 쪽
 * 코너의 라운드를 0으로 떨궈 두 바가 틈 없이 물리게 하는 데 쓴다(§7-28).
 *
 * ⚠️ **판정은 픽셀이 아니라 시각(ms)으로 한다.** 픽셀 기준이면 줌·창 크기가 바뀔 때마다 다시
 * 계산해야 하고, 줌 경계에서 같은 바의 코너 모양이 깜빡인다. 시각 기준이면 **인접은 데이터의
 * 성질**이라 로드 시 한 번 계산하면 끝이고 뷰 변화에 흔들리지 않는다.
 *
 * ⚠️ 앞뒤 짝을 이웃끼리 비교하지 않고 **시작/종료 시각 집합으로 조회**한다 — OVERLAY 모드에서는
 * 박스가 겹칠 수 있어 "배열에서 바로 앞 원소"가 실제 왼쪽 이웃이라는 보장이 없다. 집합 조회는
 * 겹침 여부와 무관하게 성립하고 O(n)이다.
 *
 * 대상에서 빼는 것:
 *   - `shtg`(결품 마름모) — 폭 0의 마름모라 이웃이라는 개념이 없다.
 *   - 길이 0 박스(`e <= s`) — 자기 시작 == 자기 종료라 **자기 자신과 맞닿았다고 오판**한다.
 *   - `jc`(작업 교체 띠) — 아래 참고. **처음엔 포함했다가 뺐다**(§7-28c).
 *
 * ⚠️ **JC를 빼는 이유**(2026-08-28 사용자 지적으로 정정): JC 띠는 `JC_BAR_HEIGHT_RATIO = 0.5`,
 *   즉 **바 높이의 절반을 세로 중앙에** 그린다. 라운드가 깎는 것은 **위아래 코너**인데 JC는 거기까지
 *   오지 않는다 — 그 코너의 이웃은 배경이다. 즉 **메울 노치가 애초에 없고**, 각지게 만들면 둥근
 *   모서리만 잃는다. 처음에 넣었던 근거("JC는 라운드 없이 딱 붙게 그리니 작업 쪽도 각져야 한다")는
 *   **같은 높이인 이웃**에나 성립하는 얘기였다.
 *   의미로도 그렇다 — JC 앞뒤로 작업은 실제로 끝나고 시작한다(`A.e = JC.s`, `B.s = JC.e`라
 *   A와 B는 서로 닿지 않는다). 둥근 모서리 = "이 작업은 여기서 끝난다"가 정확한 신호다.
 *   old가 JC에 `margin:0` + `border-left/right: 0 none`을 준 것은 **JC가 자기 작업에 붙게** 하려는
 *   것이었지 작업끼리 붙이려는 게 아니었다.
 *   → 결과적으로 `[A][JC][B]`(양 끝 둥글게)와 `[A][B]`(각지게)가 화면에서 구별되고,
 *     그 차이가 실제 데이터의 차이와 일치한다.
 *
 * 원본 배열의 원소를 그 자리에서 수정한다(`buildBoxes` 직후 1회 호출 전제).
 */
/**
 * **떨어진 JC 와 주인 작업 사이의 틈**을 모은다 — 연결선(§7-40 A안)을 그릴 구간이다.
 *
 * 엔진은 JC 를 활동에 못 붙일 때가 있다(§7-40 원인 ①~⑤). 그때 검정 띠만 남으면 **어느 작업의
 * 것인지 알 수 없다.** 붙어 있으면(`틈 <= 0`) 빈 배열이라 호출부가 아무것도 안 그린다.
 *
 * ⚠️ **주인이 같은 목록에 있을 때만** 잇는다 — 축 밖으로 잘려 나간 작업까지 쫓아가면 화면 끝에서
 * 시작하는 선이 생긴다. 주인을 못 찾으면 조용히 건너뛴다.
 *
 * @returns `[{ s, e }]` — 틈의 시각 구간. 박스가 아니다.
 */
export const jcLinkGapsOf = (boxes = []) => {
  // **JC 가 없는 행에서는 Map 을 만들지 않는다** — 매 프레임 · 모든 행에서 도는 자리라
  // 대부분의 행(JC 없음)이 값싼 한 번 훑기로 끝나야 한다(§7-49).
  if (!boxes.some(box => box.type === "jc")) return [];

  const owners = new Map();
  boxes.forEach(box => {
    if (box.type !== "jc" && box.id) owners.set(box.id, box);
  });

  const gaps = [];
  boxes.forEach(box => {
    if (box.type !== "jc") return;
    const owner = owners.get(groupKeyOf(box.id));
    if (!owner) return;

    // prev 는 JC 끝 ~ 작업 시작, next 는 작업 끝 ~ JC 시작.
    const gap = box.jcKind === "next"
      ? { s: owner.e, e: box.s }
      : { s: box.e, e: owner.s };
    if (gap.e > gap.s) gaps.push(gap);
  });

  return gaps;
};

const abutmentTarget = (box) => (
  box.type !== "shtg" && box.type !== "jc" && box.e > box.s
);

export const markAbutment = (boxes = []) => {
  const starts = new Set();
  const ends = new Set();

  boxes.forEach(box => {
    if (!abutmentTarget(box)) return;
    starts.add(box.s);
    ends.add(box.e);
  });

  boxes.forEach(box => {
    if (!abutmentTarget(box)) return;
    box.abutPrev = ends.has(box.s);      // 내 시작에서 끝나는 박스가 있다
    box.abutNext = starts.has(box.e);    // 내 끝에서 시작하는 박스가 있다
  });

  return boxes;
};

/**
 * 구간 목록을 트랙(레인)에 배정한다 — 겹치지 않는 것끼리 같은 트랙에 눕히는 그리디 패킹.
 * 입력은 시작시각 오름차순이어야 한다(정렬은 호출부 책임 — 데이터 로드 시 한 번만 하면 된다).
 * 반환은 새 배열이며 각 원소에 `track`이 붙는다. 원본은 건드리지 않는다.
 */
export const packTracks = (boxes = []) => {
  const trackEnds = [];        // 트랙별 마지막 종료시각
  const packed = boxes.map(box => {
    const start = box.s;
    let track = trackEnds.findIndex(end => end <= start);

    if (track === -1) {
      track = trackEnds.length;
      trackEnds.push(box.e);
    } else {
      trackEnds[track] = box.e;
    }

    return { ...box, track };
  });

  return { boxes: packed, trackCount: Math.max(1, trackEnds.length) };
};

/**
 * 행 배열 → 레이아웃(높이·누적 top·전체 높이). 겹침 모드에 따라 트랙 수가 달라진다.
 *  - overlay: 트랙 1개 고정 → 모든 행이 baseRowHeight
 *  - stack:   트랙 수 × trackHeight + 여백, 단 baseRowHeight 이상
 */
export const buildRowLayout = (rows = [], {
  overlapMode = OVERLAP_MODE.OVERLAY,
  baseRowHeight = 28,
  trackHeight = 22,
  paddingY = 3,
  maxTracks = 12          // 데이터 이상치로 행이 화면을 삼키는 것을 막는 상한
} = {}) => {
  let top = 0;

  const laidOut = rows.map(row => {
    const stack = overlapMode === OVERLAP_MODE.STACK;
    const packed = stack ? packTracks(row.boxes ?? []) : null;
    const trackCount = stack ? Math.min(packed.trackCount, maxTracks) : 1;
    const height = stack
      ? Math.max(baseRowHeight, trackCount * trackHeight + paddingY * 2)
      : baseRowHeight;

    const laid = {
      ...row,
      boxes: stack ? packed.boxes : (row.boxes ?? []),
      trackCount,
      height,
      top
    };

    top += height;
    return laid;
  });

  return { rows: laidOut, totalHeight: top };
};

/** 스크롤 y → 행 인덱스(누적 top 기준 이분탐색). 없으면 -1. */
export const findRowIndexAtY = (rows, y) => {
  let lo = 0;
  let hi = rows.length - 1;

  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const row = rows[mid];
    if (y < row.top) hi = mid - 1;
    else if (y >= row.top + row.height) lo = mid + 1;
    else return mid;
  }
  return -1;
};

/** 보이는 행 범위 [first, last] — 위/아래로 buffer행씩 더 그려 스크롤 시 빈틈을 없앤다. */
export const visibleRowRange = (rows, scrollTop, viewportHeight, buffer = 2) => {
  if (!rows.length) return { first: 0, last: -1 };

  const firstHit = findRowIndexAtY(rows, scrollTop);
  const lastHit = findRowIndexAtY(rows, scrollTop + viewportHeight);

  const first = Math.max(0, (firstHit === -1 ? 0 : firstHit) - buffer);
  const last = Math.min(rows.length - 1, (lastHit === -1 ? rows.length - 1 : lastHit) + buffer);
  return { first, last };
};

// ── 트리(OrderGantt) ────────────────────────────────────────────────────────
//
// 전체 평탄화 목록(각 행에 `path`/`level`)에서 **펼쳐진 조상만 가진 행**을 골라낸다.
// 펼침 상태는 화면이 Set<rowKey>로 소유한다(그리드가 없으니 우리가 관리).

/** 부모 rowKey — path 마지막 세그먼트를 뗀 것. 루트는 null. */
export const parentKeyOf = (row, sep) => (
  row.path.length <= 1 ? null : row.path.slice(0, -1).join(sep)
);

/**
 * 펼쳐진 것만 남긴 표시 행 목록.
 * `hasChildren`도 같이 채워 좌측 pane이 ▸/▾를 그릴 수 있게 한다.
 */
export const visibleTreeRows = (allRows = [], expandedKeys, sep) => {
  const childCount = new Map();
  allRows.forEach(row => {
    const parent = parentKeyOf(row, sep);
    if (parent != null) childCount.set(parent, (childCount.get(parent) ?? 0) + 1);
  });

  const visible = [];
  // 조상 중 하나라도 접혀 있으면 건너뛴다. path 순서가 곧 DFS 순서라 조상 판정은 앞선 결과를 쓴다.
  const hiddenUnder = new Set();

  allRows.forEach(row => {
    const parent = parentKeyOf(row, sep);
    if (parent != null && (hiddenUnder.has(parent) || !expandedKeys.has(parent))) {
      hiddenUnder.add(row.rowKey);
      return;
    }
    visible.push({ ...row, hasChildren: (childCount.get(row.rowKey) ?? 0) > 0 });
  });

  return visible;
};

/** 어떤 행의 하위 전체 rowKey(재귀) — 펼치기/접기 일괄 처리에 쓴다. */
export const descendantKeys = (allRows, rowKey, sep) => {
  const prefix = `${rowKey}${sep}`;
  return allRows.filter(row => row.rowKey.startsWith(prefix)).map(row => row.rowKey);
};
